import { Injectable, signal, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { API } from './api/api';

@Injectable({
  providedIn: 'root',
})
export class ClientSettingService {
  private _isInitialized = false;

  clientSettings: WritableSignal<Record<string, string> | null> = signal<Record<string, string> | null>(null);

  constructor(private readonly api: API) {}

  updateClientSettings(data: Record<string, string> | null) {
    if (data) {
      this.clientSettings.set({ ...data });
    } else {
      this.clientSettings.set(null);
    }
  }

  async getClientSettingsFromServer(): Promise<void> {
    const clientSettings = await firstValueFrom(
      this.api.shared_Client_retrieveSettings().pipe(
        take(1),
        catchError(err => {
          throw new Error(err);
        })
      )
    );
    this._isInitialized = true;
    this.updateClientSettings(clientSettings);
  }

  async setClientSetting(key: string, value: string): Promise<void> {
    if (key == null || value == null) {
      throw new Error('Missing Key/Value pair definition');
    }
    try {
      const settings = this.clientSettings() ?? {};
      settings[key] = value;
      const updatedSettings: Record<string, any> = await firstValueFrom(
        this.api.shared_Client_updateSettings(settings).pipe(
          take(1),
          catchError(err => {
            throw new Error(err);
          })
        )
      );
      this.updateClientSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating user setting:', error);
      throw error;
    }
  }

  async clearClientSetting(key: string): Promise<void> {
    try {
      const settings = this.clientSettings() ?? {};
      if (settings[key]) {
        delete settings[key];
      }
      const updatedSettings: Record<string, any> = await firstValueFrom(
        this.api.shared_Client_updateSettings(settings).pipe(
          take(1),
          catchError(err => {
            throw new Error(err);
          })
        )
      );
      this.updateClientSettings(updatedSettings);
    } catch (error) {
      console.error('Error clearing user setting:', error);
      throw error;
    }
  }

  async getAllClientSettings(): Promise<Record<string, string>> {
    return Promise.resolve(this.clientSettings());
  }

  async getClientSetting(key: string): Promise<string | null> {
    if (!this._isInitialized) {
      await this.getClientSettingsFromServer();
    }
    return Promise.resolve(this.clientSettings()?.[key] ?? null);
  }

  async clearAllClientSettings(): Promise<void> {
    const updatedSettings: Record<string, string> = await firstValueFrom(
      this.api.shared_Client_clearSettings().pipe(
        take(1),
        catchError(err => {
          throw new Error(err);
        })
      )
    );
    this.updateClientSettings(updatedSettings);
  }
}
